import { useMemo } from 'react';
import type { TopRadio } from '@/mocks/superAdminAnalytics';
import { topListeners, topFollowers, topMessages, topGrowth, topLives, topStreamDown, topActive, topScore } from '@/mocks/superAdminAnalytics';

interface RadioDetailModalProps {
  radio: TopRadio;
  onClose: () => void;
}

interface RadioDetailData {
  listeners: number;
  listenersChange: number;
  followers: number;
  followersChange: number;
  messages: number;
  messagesChange: number;
  growth: number;
  growthChange: number;
  lives: number;
  livesChange: number;
  streamDown: number;
  streamDownChange: number;
  active: number;
  activeChange: number;
  score: number;
  scoreChange: number;
}

function findMetric(data: TopRadio[], radioId: string): { value: number; change: number } {
  const found = data.find((r) => r.radioId === radioId);
  return found ? { value: found.value, change: found.changePercent } : { value: 0, change: 0 };
}

const planLabels: Record<string, string> = { free: 'Free', pro: 'Pro', premium: 'Premium' };
const categoryLabels: Record<string, string> = {
  musical: 'Musical', noticias: 'Noticias', deportiva: 'Deportiva',
  cultural: 'Cultural', juvenil: 'Juvenil', religiosa: 'Religiosa', comunitaria: 'Comunitaria',
};
const statusLabels: Record<string, string> = { activa: 'Activa', inactiva: 'Inactiva', suspendida: 'Suspendida' };
const statusColors: Record<string, string> = {
  activa: 'bg-emerald-100 text-emerald-700',
  inactiva: 'bg-amber-100 text-amber-700',
  suspendida: 'bg-red-100 text-red-700',
};

export default function RadioDetailModal({ radio, onClose }: RadioDetailModalProps) {
  const detail: RadioDetailData = useMemo(() => {
    return {
      listeners: findMetric(topListeners, radio.radioId).value,
      listenersChange: findMetric(topListeners, radio.radioId).change,
      followers: findMetric(topFollowers, radio.radioId).value,
      followersChange: findMetric(topFollowers, radio.radioId).change,
      messages: findMetric(topMessages, radio.radioId).value,
      messagesChange: findMetric(topMessages, radio.radioId).change,
      growth: findMetric(topGrowth, radio.radioId).value,
      growthChange: findMetric(topGrowth, radio.radioId).change,
      lives: findMetric(topLives, radio.radioId).value,
      livesChange: findMetric(topLives, radio.radioId).change,
      streamDown: findMetric(topStreamDown, radio.radioId).value,
      streamDownChange: findMetric(topStreamDown, radio.radioId).change,
      active: findMetric(topActive, radio.radioId).value,
      activeChange: findMetric(topActive, radio.radioId).change,
      score: findMetric(topScore, radio.radioId).value,
      scoreChange: findMetric(topScore, radio.radioId).change,
    };
  }, [radio.radioId]);

  function fmt(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString();
  }

  const metrics: { label: string; value: string; change: number; icon: string; suffix?: string }[] = [
    { label: 'Oyentes/mes', value: fmt(detail.listeners), change: detail.listenersChange, icon: 'ri-user-heart-line' },
    { label: 'Seguidores', value: fmt(detail.followers), change: detail.followersChange, icon: 'ri-user-star-line' },
    { label: 'Mensajes Cabina', value: fmt(detail.messages), change: detail.messagesChange, icon: 'ri-message-2-line' },
    { label: 'Crecimiento', value: fmt(detail.growth), change: detail.growthChange, icon: 'ri-line-chart-line', suffix: '%' },
    { label: 'Lives', value: fmt(detail.lives), change: detail.livesChange, icon: 'ri-live-line' },
    { label: 'Horas Offline', value: fmt(detail.streamDown), change: detail.streamDownChange, icon: 'ri-cloud-off-line', suffix: 'h' },
    { label: 'Índice Actividad', value: fmt(detail.active), change: detail.activeChange, icon: 'ri-flashlight-line' },
    { label: 'Radio Score', value: `${detail.score}`, change: detail.scoreChange, icon: 'ri-medal-line', suffix: '/100' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-background-50 border border-background-200 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-background-50 border-b border-background-200 px-6 py-4 rounded-t-2xl z-10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                <i className="ri-radio-line text-amber-600 text-xl"></i>
              </div>
              <div className="min-w-0">
                <h3 className="text-foreground-950 font-bold text-sm">{radio.radioName}</h3>
                <p className="text-foreground-400 text-[11px]">{radio.frequency}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0 cursor-pointer hover:bg-background-200 transition-colors"
            >
              <i className="ri-close-line text-foreground-500 text-sm"></i>
            </button>
          </div>

          {/* Tags */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span className="px-2 py-0.5 rounded-md bg-primary-100 text-primary-700 text-[10px] font-semibold">
              {planLabels[radio.plan] || radio.plan}
            </span>
            <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-[10px] font-semibold">
              {categoryLabels[radio.category] || radio.category}
            </span>
            <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold flex items-center gap-1">
              <span className={`w-1.5 h-1.5 rounded-full ${radio.status === 'activa' ? 'bg-emerald-500' : radio.status === 'inactiva' ? 'bg-amber-500' : 'bg-red-500'}`}></span>
              {statusLabels[radio.status] || radio.status}
            </span>
            <span className="px-2 py-0.5 rounded-md bg-background-100 text-foreground-500 text-[10px] font-medium">
              {radio.country}
            </span>
          </div>
        </div>

        {/* Body — Metrics Grid */}
        <div className="px-6 py-5">
          <h4 className="text-foreground-800 text-xs font-semibold mb-3">Métricas Detalladas</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {metrics.map((m) => {
              const isPositive = m.change >= 0;
              return (
                <div key={m.label} className="bg-background-100 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <i className={`${m.icon} text-foreground-400 text-[11px]`}></i>
                    <span className="text-foreground-400 text-[9px] font-medium">{m.label}</span>
                  </div>
                  <p className="text-foreground-950 font-bold text-sm leading-tight">
                    {m.value}{m.suffix || ''}
                  </p>
                  <span className={`inline-flex items-center gap-0.5 text-[9px] font-semibold ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
                    <i className={`${isPositive ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-[8px]`}></i>
                    {Math.abs(m.change).toFixed(1)}%
                  </span>
                </div>
              );
            })}
          </div>

          {/* Score bar */}
          <div className="mt-5 bg-background-100 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <i className="ri-medal-line text-amber-500 text-sm"></i>
                <span className="text-foreground-800 text-xs font-semibold">Radio Score</span>
              </div>
              <span className="text-foreground-950 font-bold text-sm">{detail.score}/100</span>
            </div>
            <div className="w-full h-2.5 bg-background-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  detail.score >= 90 ? 'bg-emerald-500' :
                  detail.score >= 70 ? 'bg-amber-500' :
                  detail.score >= 40 ? 'bg-orange-500' :
                  'bg-red-500'
                }`}
                style={{ width: `${Math.min(detail.score, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* Bottom info */}
          <div className="mt-5 p-4 bg-accent-50 border border-accent-200 rounded-xl">
            <div className="flex items-center gap-2">
              <i className="ri-information-line text-accent-500 text-sm"></i>
              <p className="text-accent-700 text-[10px]">
                Los datos mostrados corresponden al período seleccionado. Para ver el historial completo de la radio, visitá la sección de Radios.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 px-6 py-3 rounded-b-2xl flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold cursor-pointer hover:bg-foreground-800 transition-colors whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}